import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    Plus,
    Trash2,
    Edit3,
    Eye,
    Loader2,
    Save,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "../../components/ui/dialog";
import { adminApi, getAdminToken, clearAdminToken } from "../../lib/api";

const emptyPost = {
    title: "",
    excerpt: "",
    content: "",
    cover_image_url: "",
    tags: [],
    published: false,
};

export default function AdminBlog() {
    const navigate = useNavigate();
    const token = getAdminToken();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // post object or null
    const [form, setForm] = useState(emptyPost);
    const [tagInput, setTagInput] = useState("");
    const [saving, setSaving] = useState(false);

    const client = adminApi(token);

    const load = async () => {
        try {
            const { data } = await client.get("/admin/blog");
            setPosts(data);
        } catch (err) {
            if (err?.response?.status === 401) {
                clearAdminToken();
                navigate("/admin");
            } else {
                toast.error("Failed to load posts.");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!token) {
            navigate("/admin");
            return;
        }
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openNew = () => {
        setEditing({ id: null });
        setForm(emptyPost);
        setTagInput("");
    };

    const openEdit = (p) => {
        setEditing(p);
        setForm({
            title: p.title,
            excerpt: p.excerpt,
            content: p.content,
            cover_image_url: p.cover_image_url || "",
            tags: p.tags || [],
            published: !!p.published,
        });
        setTagInput((p.tags || []).join(", "));
    };

    const save = async () => {
        if (!form.title.trim() || !form.excerpt.trim() || !form.content.trim()) {
            toast.error("Title, excerpt and content are required.");
            return;
        }
        setSaving(true);
        try {
            const tags = tagInput
                .split(",")
                .map((t) => t.trim())
                .filter(Boolean);
            const payload = { ...form, tags };
            if (editing.id) {
                await client.put(`/admin/blog/${editing.id}`, payload);
                toast.success("Post updated.");
            } else {
                await client.post("/admin/blog", payload);
                toast.success("Post created.");
            }
            setEditing(null);
            load();
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Save failed.",
            );
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        if (!confirm("Delete this post? This cannot be undone.")) return;
        try {
            await client.delete(`/admin/blog/${id}`);
            toast.success("Post deleted.");
            load();
        } catch {
            toast.error("Delete failed.");
        }
    };

    return (
        <div className="min-h-screen bg-[#F3EFE9]">
            <div className="bg-white border-b border-[#E6DFD3]">
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
                    <Link
                        to="/admin/dashboard"
                        className="inline-flex items-center gap-2 text-sm text-[#4A5A58] hover:text-[#1A5C53]"
                    >
                        <ArrowLeft size={16} /> Dashboard
                    </Link>
                    <button
                        onClick={openNew}
                        data-testid="new-blog-post-btn"
                        className="inline-flex items-center gap-2 bg-[#1A5C53] text-white hover:bg-[#13423B] rounded-full px-5 py-2.5 text-sm font-medium"
                    >
                        <Plus size={16} /> New Post
                    </button>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
                <h1 className="font-serif text-4xl text-[#0D1B1A]">
                    Blog Manager
                </h1>
                <p className="text-[#4A5A58] mt-1">
                    Create, edit and publish posts.
                </p>

                <div className="mt-8 bg-white border border-[#E6DFD3] rounded-2xl overflow-hidden">
                    {loading ? (
                        <div className="p-10 text-center text-[#758684]">
                            Loading…
                        </div>
                    ) : posts.length === 0 ? (
                        <div className="p-10 text-center">
                            <p className="text-[#758684]">No posts yet.</p>
                            <button
                                onClick={openNew}
                                className="mt-4 inline-flex items-center gap-2 text-[#1A5C53] hover:underline"
                            >
                                <Plus size={14} /> Create your first post
                            </button>
                        </div>
                    ) : (
                        <ul className="divide-y divide-[#E6DFD3]">
                            {posts.map((p) => (
                                <li
                                    key={p.id}
                                    data-testid={`blog-row-${p.slug}`}
                                    className="flex items-center gap-4 p-5 hover:bg-[#FDFAF5] transition-colors"
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-3 flex-wrap">
                                            <div className="font-serif text-lg text-[#0D1B1A] truncate">
                                                {p.title}
                                            </div>
                                            {p.published ? (
                                                <Badge className="bg-[#1A5C53] text-white hover:bg-[#1A5C53]">
                                                    Published
                                                </Badge>
                                            ) : (
                                                <Badge
                                                    variant="outline"
                                                    className="border-[#E6DFD3] text-[#758684]"
                                                >
                                                    Draft
                                                </Badge>
                                            )}
                                        </div>
                                        <div className="text-xs text-[#758684] mt-1 truncate">
                                            /{p.slug} ·{" "}
                                            {new Date(p.updated_at).toLocaleDateString(
                                                "en-IN",
                                            )}
                                        </div>
                                    </div>
                                    {p.published && (
                                        <a
                                            href={`/blog/${p.slug}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="p-2 text-[#4A5A58] hover:text-[#1A5C53]"
                                            title="View"
                                        >
                                            <Eye size={16} />
                                        </a>
                                    )}
                                    <button
                                        onClick={() => openEdit(p)}
                                        data-testid={`edit-blog-${p.slug}`}
                                        className="p-2 text-[#4A5A58] hover:text-[#1A5C53]"
                                        title="Edit"
                                    >
                                        <Edit3 size={16} />
                                    </button>
                                    <button
                                        onClick={() => remove(p.id)}
                                        data-testid={`delete-blog-${p.slug}`}
                                        className="p-2 text-[#9B2226] hover:text-[#7a1a1d]"
                                        title="Delete"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            <Dialog
                open={!!editing}
                onOpenChange={(o) => !o && setEditing(null)}
            >
                <DialogContent className="bg-[#FDFAF5] border-[#E6DFD3] max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl">
                            {editing?.id ? "Edit Post" : "New Post"}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4 mt-2">
                        <div>
                            <Label>Title</Label>
                            <Input
                                data-testid="blog-title-input"
                                value={form.title}
                                onChange={(e) =>
                                    setForm({ ...form, title: e.target.value })
                                }
                                className="mt-1.5 bg-white border-[#E6DFD3]"
                                placeholder="The hidden cost of 'minimum due'"
                            />
                        </div>
                        <div>
                            <Label>Excerpt</Label>
                            <Textarea
                                data-testid="blog-excerpt-input"
                                value={form.excerpt}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        excerpt: e.target.value,
                                    })
                                }
                                className="mt-1.5 bg-white border-[#E6DFD3]"
                                placeholder="One-paragraph summary shown on the blog list."
                            />
                        </div>
                        <div>
                            <Label>Cover Image URL</Label>
                            <Input
                                data-testid="blog-cover-input"
                                value={form.cover_image_url}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        cover_image_url: e.target.value,
                                    })
                                }
                                className="mt-1.5 bg-white border-[#E6DFD3]"
                                placeholder="https://..."
                            />
                        </div>
                        <div>
                            <Label>Content (Markdown)</Label>
                            <Textarea
                                data-testid="blog-content-input"
                                value={form.content}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        content: e.target.value,
                                    })
                                }
                                className="mt-1.5 bg-white border-[#E6DFD3] min-h-[240px] font-mono text-sm"
                                placeholder="## Heading&#10;&#10;Write in markdown..."
                            />
                        </div>
                        <div>
                            <Label>Tags (comma-separated)</Label>
                            <Input
                                data-testid="blog-tags-input"
                                value={tagInput}
                                onChange={(e) => setTagInput(e.target.value)}
                                className="mt-1.5 bg-white border-[#E6DFD3]"
                                placeholder="debt, emi, case-study"
                            />
                        </div>
                        <div className="flex items-center gap-3 pt-2">
                            <Switch
                                checked={form.published}
                                onCheckedChange={(v) =>
                                    setForm({ ...form, published: v })
                                }
                                data-testid="blog-publish-toggle"
                            />
                            <Label className="!mb-0 cursor-pointer">
                                {form.published
                                    ? "Published (public)"
                                    : "Draft (not public)"}
                            </Label>
                        </div>
                    </div>

                    <DialogFooter className="mt-6">
                        <button
                            onClick={() => setEditing(null)}
                            className="text-[#4A5A58] hover:text-[#0D1B1A] px-5 py-2"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={save}
                            disabled={saving}
                            data-testid="blog-save-btn"
                            className="inline-flex items-center gap-2 bg-[#1A5C53] text-white hover:bg-[#13423B] rounded-md px-6 py-2.5 font-medium disabled:opacity-60"
                        >
                            {saving ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : (
                                <Save size={16} />
                            )}
                            {editing?.id ? "Save changes" : "Create post"}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
